/**
 * Tournament portal phase/lifecycle utilities.
 *
 * portalStatus progression:
 *   signup -> roster-locked -> tee-sheet-published -> gameday -> completed -> recap-posted
 *
 * Each tab's visibility and interactivity depends on the current portalStatus.
 */

const PHASE_ORDER = [
  'signup',
  'roster-locked',
  'tee-sheet-published',
  'gameday',
  'completed',
  'recap-posted',
];

const PHASE_LABELS = {
  'signup': 'Signup Open',
  'roster-locked': 'Roster Locked',
  'tee-sheet-published': 'Tee Sheet Published',
  'gameday': 'Gameday',
  'completed': 'Completed',
  'recap-posted': 'Recap Posted',
};

/**
 * Returns true when the current phase is at or past the required phase.
 */
const isAtOrPast = (current, required) => {
  const currentIdx = PHASE_ORDER.indexOf(current);
  const requiredIdx = PHASE_ORDER.indexOf(required);
  if (currentIdx === -1 || requiredIdx === -1) return false;
  return currentIdx >= requiredIdx;
};

/**
 * Returns true when the current phase exactly matches.
 */
const isExactly = (current, phase) => current === phase;

/**
 * Tab visibility rules keyed by tab id.
 * Each entry returns { visible, editable } for a given portalStatus.
 */
const TAB_CONFIG = {
  activity: {
    visible: () => true,
    editable: () => false, // read-only for players
  },
  request: {
    visible: (status) => isAtOrPast(status, 'roster-locked'),
    editable: (status) =>
      isExactly(status, 'roster-locked') || isExactly(status, 'tee-sheet-published'),
  },
  teesheet: {
    visible: (status) => isAtOrPast(status, 'tee-sheet-published'),
    editable: () => false,
  },
  sideaction: {
    visible: (status) => isAtOrPast(status, 'roster-locked'),
    editable: (status) => !isAtOrPast(status, 'recap-posted'),
  },
  recap: {
    visible: (status) => isAtOrPast(status, 'recap-posted'),
    editable: () => false,
  },
  feedback: {
    visible: (status) => isAtOrPast(status, 'completed'),
    editable: (status) => isExactly(status, 'completed'),
  },
};

/**
 * Get visible tabs for a given portalStatus.
 * Returns an array of { id, label, editable } objects.
 */
export const getVisibleTabs = (portalStatus) => {
  const tabMeta = {
    activity: 'Activity',
    request: 'Request',
    teesheet: 'Tee Sheet',
    sideaction: 'Side Action',
    recap: 'Recap',
    feedback: 'Feedback',
  };

  return Object.entries(TAB_CONFIG)
    .filter(([, config]) => config.visible(portalStatus))
    .map(([id, config]) => ({
      id,
      label: tabMeta[id],
      editable: config.editable(portalStatus),
    }));
};

/**
 * Check whether a specific tab is visible for a portalStatus.
 */
export const isTabVisible = (tabId, portalStatus) => {
  const config = TAB_CONFIG[tabId];
  return config ? config.visible(portalStatus) : false;
};

/**
 * Check whether a specific tab is editable for a portalStatus.
 */
export const isTabEditable = (tabId, portalStatus) => {
  const config = TAB_CONFIG[tabId];
  return config ? config.editable(portalStatus) : false;
};

/**
 * Whether the tee sheet tab should show a GolfGenius link-out.
 */
export const shouldShowGolfGeniusLink = (portalStatus) =>
  isExactly(portalStatus, 'gameday');

/**
 * Get the next phase in the lifecycle (for TD stepper).
 * Returns null if already at the last phase.
 */
export const getNextPhase = (current) => {
  const idx = PHASE_ORDER.indexOf(current);
  if (idx === -1 || idx >= PHASE_ORDER.length - 1) return null;
  return PHASE_ORDER[idx + 1];
};

/**
 * Get the previous phase (for TD rewind).
 * Returns null if already at the first phase.
 */
export const getPreviousPhase = (current) => {
  const idx = PHASE_ORDER.indexOf(current);
  if (idx <= 0) return null;
  return PHASE_ORDER[idx - 1];
};

/**
 * Get a human-readable label for a phase.
 */
export const getPhaseLabel = (phase) => PHASE_LABELS[phase] || phase;

/**
 * Get all phases with their index for rendering a stepper.
 */
export const getAllPhases = () =>
  PHASE_ORDER.map((phase, index) => ({
    id: phase,
    label: PHASE_LABELS[phase],
    index,
  }));

/**
 * Get the index of the current phase (0-based).
 */
export const getPhaseIndex = (phase) => PHASE_ORDER.indexOf(phase);

export { PHASE_ORDER, PHASE_LABELS };
